import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Phone, Calendar, MessageCircle, ArrowUpRight } from 'lucide-react';
import img4 from '../assets/img4.jpg';
import { useLanguage } from '../context/LanguageContext';

const Hero = () => {
  const { t, language } = useLanguage();

  const trust1 = language === 'mr' ? '२५+ वर्षे' : (language === 'kn' ? '೨೫+ ವರ್ಷ' : '25+ Yrs');
  const trust1Sub = language === 'mr' ? 'अनुभव व सेवा' : (language === 'kn' ? 'ಉತ್ತಮ ಸೇವೆ' : 'Excellence');
  
  const trust2 = language === 'mr' ? '६ शाखा' : (language === 'kn' ? '೬ ಶಾಖೆಗಳು' : '6 Centers');
  const trust2Sub = language === 'mr' ? 'महा. व कर्नाटक' : (language === 'kn' ? 'ಮಹಾ. ಮತ್ತು ಕನಾ.' : 'MH & KA');
  
  const trust3 = language === 'mr' ? '५०,०००+' : (language === 'kn' ? '೫೦,೦೦೦+' : '50k+');
  const trust3Sub = language === 'mr' ? 'समाधानी रुग्ण' : (language === 'kn' ? 'ಸಂತೃಪ್ತ ರೋಗಿಗಳು' : 'Patients');

  return (
    <div className="relative bg-[#E6F0FA] pt-28 pb-20 sm:pt-32 sm:pb-24 lg:pt-40 lg:pb-40 overflow-hidden flex items-center min-h-[85vh] sm:min-h-[90vh]">
      {/* Full-bleed background image covering everything */}
      <div className="absolute inset-0 w-full h-full z-0 overflow-hidden">
        <img
          src={img4}
          alt="Eye Care Professional"
          className="w-full h-full object-cover object-[78%_center] sm:object-center lg:object-right"
        />
        {/* Fresh blue tint overlay */}
        <div className="absolute inset-0 bg-clinical/15 mix-blend-multiply"></div>
        
        {/* Desktop gradient overlay */}
        <div className="hidden lg:block absolute inset-0 bg-gradient-to-r from-[#E6F0FA] via-[#E6F0FA]/90 to-transparent"></div>
        
        {/* Mobile & Tablet enhanced gradient for 100% crystal-clear readability */}
        <div className="lg:hidden absolute inset-0 bg-gradient-to-b from-[#E6F0FA] via-[#E6F0FA]/85 to-[#E6F0FA]/50"></div>
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="w-full lg:w-[60%] xl:w-[50%]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Top Badge */}
            <div className="inline-flex items-center space-x-2 sm:space-x-3 mb-4 sm:mb-6 px-3.5 py-1.5 sm:px-0 sm:py-0 rounded-full sm:rounded-none bg-white/75 sm:bg-transparent backdrop-blur-md sm:backdrop-blur-none border border-teal/30 sm:border-0 shadow-2xs sm:shadow-none max-w-full">
              <span className="w-2 h-2 rounded-full bg-teal animate-pulse sm:hidden shrink-0"></span>
              <span className="hidden sm:inline-block w-8 h-[2px] bg-teal"></span>
              <span className="text-teal font-extrabold sm:font-bold tracking-wider sm:tracking-widest text-[10px] sm:text-xs uppercase truncate">
                {t('hero.society')}
              </span>
              <span className="hidden sm:inline-block w-8 h-[2px] bg-teal"></span>
            </div>

            {/* Main Heading */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-[5.5rem] font-extrabold text-navy leading-[1.08] mb-3.5 sm:mb-6 tracking-tight">
              {t('hero.titleLine1')} <br className="hidden sm:inline" />
              <span className="text-clinical">{t('hero.titleLine2')}</span>
            </h1>

            {/* Subtitle */}
            <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed max-w-lg mb-6 sm:mb-8 font-normal">
              {t('hero.description')}
            </p>
          </motion.div>

          {/* Action Buttons Stack (Creative & Mobile-Optimized) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center w-full max-w-md lg:max-w-none"
          >
            {/* Primary Action Button */}
            <Link
              to="/appointment"
              className="bg-clinical hover:bg-navy text-white px-6 py-3.5 sm:px-7 rounded-xl sm:rounded-sm font-bold sm:font-semibold transition-all flex items-center justify-center shadow-lg shadow-clinical/25 group text-sm sm:text-base active:scale-98"
            >
              <Calendar size={18} className="mr-2 opacity-90" />
              <span>{t('hero.bookAppointment')}</span>
              <ArrowUpRight size={18} className="ml-2 opacity-80 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>

            {/* Quick Contact Twin Row on Mobile */}
            <div className="grid grid-cols-2 gap-2.5 sm:flex sm:gap-4">
              <a
                href="tel:+919876543210"
                className="bg-white/95 hover:bg-white text-navy px-3.5 sm:px-6 py-3 sm:py-3.5 rounded-xl sm:rounded-sm font-bold sm:font-semibold transition-all flex items-center justify-center shadow-xs border border-gray-200/80 text-xs sm:text-sm active:scale-98"
              >
                <Phone size={16} className="mr-1.5 text-navy opacity-75 shrink-0" />
                <span>{t('hero.callNow')}</span>
              </a>

              <a
                href="https://wa.me/919876543210"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/95 hover:bg-white text-navy px-3.5 sm:px-6 py-3 sm:py-3.5 rounded-xl sm:rounded-sm font-bold sm:font-semibold transition-all flex items-center justify-center shadow-xs border border-gray-200/80 text-xs sm:text-sm active:scale-98"
              >
                <MessageCircle size={16} className="mr-1.5 text-[#25D366] shrink-0" />
                <span>{t('hero.whatsapp')}</span>
              </a>
            </div>
          </motion.div>

          {/* Creative Trust Indicators on Mobile */}
          <div className="grid grid-cols-3 gap-2 mt-6 pt-5 border-t border-gray-300/50 lg:hidden">
            <div className="bg-white/80 backdrop-blur-xs p-2.5 rounded-xl border border-white/70 text-center shadow-2xs">
              <span className="text-clinical font-black text-sm block">{trust1}</span>
              <span className="text-[10px] text-gray-500 font-semibold leading-tight block">{trust1Sub}</span>
            </div>
            <div className="bg-white/80 backdrop-blur-xs p-2.5 rounded-xl border border-white/70 text-center shadow-2xs">
              <span className="text-navy font-black text-sm block">{trust2}</span>
              <span className="text-[10px] text-gray-500 font-semibold leading-tight block">{trust2Sub}</span>
            </div>
            <div className="bg-white/80 backdrop-blur-xs p-2.5 rounded-xl border border-white/70 text-center shadow-2xs">
              <span className="text-teal font-black text-sm block">{trust3}</span>
              <span className="text-[10px] text-gray-500 font-semibold leading-tight block">{trust3Sub}</span>
            </div>
          </div>


        </div>
      </div>
    </div>
  );
};

export default Hero;
